import PropTypes from 'prop-types';
import { Component } from 'react';
import { compose } from 'redux';
import createDebug from 'debug';
import { withCurrentUserFeaturesDEPRECATED } from '../../features/currentUser';
import { createWebSocket } from './createWebSocket';
import { REALTIME_SOCKET_V3 } from '../../shared/lib/api';
import { withDeprecatedCurrentArtist } from '../../features/artists';

const debug = createDebug('components:ArtistLiveListeners');

class ArtistLiveListenersComponent extends Component {
  static propTypes = {
    artist: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,

    getDocumentHidden: PropTypes.func,
    WebSocketImplementation: PropTypes.func,
    features: PropTypes.objectOf(PropTypes.string),
    render: PropTypes.func.isRequired,
  };

  static defaultProps = {
    getDocumentHidden: () => document.hidden,
    features: {},
  };

  state = {
    listeners: -1,
  };

  // used for exponential backoff when trying to open a new websocket after the previous one closed unexpectetly
  errorCount = 0;

  componentDidMount() {
    this.createSocket();
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  componentWillUnmount() {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);

    if (this.socket) {
      this.socket.removeEventListener('message', this.onSocketMessage);
      this.socket.close();
      this.socket = undefined;
    }

    window.clearTimeout(this.reconnectTimeout);
  }

  onVisibilityChange = () => {
    const hidden = this.props.getDocumentHidden();
    if (hidden && this.socket) {
      this.closeSocket();
    } else if (!hidden && !this.socket) {
      this.createSocket();
    }
  };

  onSocketMessage = ({ data }) => {
    const listeners = JSON.parse(data).data;
    if (listeners !== this.state.listeners) this.setState({ listeners });
  };

  onSocketClose = ({ wasClean, code, reason }) => {
    window.clearTimeout(this.reconnectTimeout);
    this.reconnectTimeout = undefined;

    if (!wasClean) {
      debug(`websocket closed with code ${code}`);
      debug(`websocket closed with reason ${reason}`);

      // exponential backoff when trying to open a new websocket, maxed at roughly a 2.5 hour timeout
      this.errorCount = Math.min(this.errorCount + 1, 16);
      this.reconnectTimeout = window.setTimeout(
        this.createSocket,
        Math.round(Math.exp(this.errorCount)),
      );
    }
  };

  getWebSocketUrl = () =>
    `${REALTIME_SOCKET_V3}/artist/listeners/${this.props.artist.id}`;

  createSocket = () => {
    const { WebSocketImplementation = window.WebSocket } = this.props;

    // if no WebSocketImplementation then exit early
    if (!WebSocketImplementation) return;

    const socketUrl = this.getWebSocketUrl();

    // Yes, we are indeed hardcoding that domain here
    // 1) iOS Safari apparently cannot handle the local proxy
    // 2) Staging does not work at all (Shibboleth IdP does not pass through websocket requests)
    this.socket = createWebSocket(socketUrl, WebSocketImplementation);
    this.socket.addEventListener('message', this.onSocketMessage);
    this.socket.addEventListener('error', error =>
      debug('websocket:error', error),
    );
    this.socket.addEventListener('open', () => {
      this.errorCount = 0;
    });
    this.socket.addEventListener('close', this.onSocketClose);
  };

  closeSocket = () => {
    this.socket.close();
    this.socket = undefined;
  };

  render() {
    const { render } = this.props;
    const { listeners } = this.state;

    return render(
      listeners,
      () => this.createSocket(),
      () => this.closeSocket(),
    );
  }
}

export const ArtistLiveListeners = compose(
  withCurrentUserFeaturesDEPRECATED,
  withDeprecatedCurrentArtist,
)(ArtistLiveListenersComponent);
